// deps
import React, { memo, useCallback } from "react";

// internal-deps
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { MenuItemsCalloutInfo } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { replaceText } from "@swiggy-private/core-ui";
import { MENU_ITEMS_CALLOUT_TEST_IDS } from "@swiggy-private/cart-test-ids";

// components
import { ContentContainer, DescriptionListContainer, ImageContainer, TitleContainer } from "./HalfCardWidget.style";

//constants
import { HALF_CARD_ACTION } from "./constants";

interface HalfCardWidgetProps {
    showHalfCard: boolean;
    halfCardInfo: MenuItemsCalloutInfo["half_card_info"];
    onClose: (action: string) => void;
    isGiftingCart?: boolean;
}

function HalfCardWidget({ showHalfCard, halfCardInfo, onClose, isGiftingCart }: HalfCardWidgetProps) {
    const onHalfCardClose = useCallback(() => {
        onClose(HALF_CARD_ACTION.CLOSE);
    }, [onClose]);

    const onCtaClick = useCallback(() => {
        onClose(HALF_CARD_ACTION.CTA);
    }, [onClose]);

    if (!showHalfCard) {
        return null;
    }

    return (
        <HalfCard isOpen={showHalfCard} onClose={onHalfCardClose} hideCloseIcon hasCustomPadding>
            <ContentContainer data-testid={MENU_ITEMS_CALLOUT_TEST_IDS.MENU_ITEMS_CALLOUT_HALF_CARD_WIDGET}>
                {!isEmpty(halfCardInfo?.image_url) && (
                    <ImageContainer
                        imageUrl={halfCardInfo?.image_url}
                        data-testid={MENU_ITEMS_CALLOUT_TEST_IDS.MENU_ITEMS_CALLOUT_HALF_CARD_IMAGE}
                    />
                )}
                {!isEmpty(halfCardInfo?.title) && (
                    <TitleContainer
                        variant="V2.heading1.neutral.black"
                        color="text_Highest_Emphasis"
                        data-testid={MENU_ITEMS_CALLOUT_TEST_IDS.MENU_ITEMS_CALLOUT_HALF_CARD_TITLE}
                        isGiftingCart={isGiftingCart}
                    >
                        {halfCardInfo?.title}
                    </TitleContainer>
                )}
                {!isEmpty(halfCardInfo?.description_list) && (
                    <DescriptionListContainer
                        data-testid={MENU_ITEMS_CALLOUT_TEST_IDS.MENU_ITEMS_CALLOUT_HALF_CARD_DESCRIPTION}
                    >
                        {halfCardInfo?.description_list.map((description, index) => (
                            <li key={index}>
                                <Text variant="V2.body1.neutral.regular" color="text_High_Emphasis">
                                    {replaceText(description)}
                                </Text>
                            </li>
                        ))}
                    </DescriptionListContainer>
                )}
                {!isEmpty(halfCardInfo?.action_text) && (
                    <FullWidthButton variant="primary" onClick={onCtaClick}>
                        {halfCardInfo?.action_text}
                    </FullWidthButton>
                )}
            </ContentContainer>
        </HalfCard>
    );
}

export default memo(HalfCardWidget);
